import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Skeleton, Typography } from 'antd';
import { ExperimentOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { SectionCard } from '@/components/SectionCard';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { RESULT_STATE_LABEL } from '@/api/runEvidence';
import { getEvaluationWorklist, type EvaluationWorklist } from '@/api/evaluationWorklist';
import { STATE_ACCENT } from '@/features/validations/RunReadingPanel';
import { STATE_ORDER, countOf, evaluationPath } from './worklistWords';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// "Evaluate this agent" (screen 01, frame B) — the entry on the agent's own page in Agent Design. The button opens the
// Scenario Intelligence Overview with this agent and the selected practice chosen; the card shows the latest evaluation
// in the same five counts the Overview leads with, so a designer sees "3 need attention" without leaving the agent.
// The counts are the server's worklist; nothing is recounted here.
// ─────────────────────────────────────────────────────────────────────────────

export function EvaluateThisAgentButton({ agentId }: { agentId: number }) {
  const navigate = useNavigate();
  return (
    <Button type="primary" icon={<ExperimentOutlined />} onClick={() => navigate(evaluationPath('', agentId))}>
      Evaluate this agent
    </Button>
  );
}

export const LatestEvaluationCard = observer(function LatestEvaluationCard({ agentId }: { agentId: number }) {
  const { themeStore, merchantContextStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const merchantId = merchantContextStore.selectedMerchantId;
  const [data, setData] = useState<EvaluationWorklist | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setData(null);
    setError(null);
    getEvaluationWorklist(agentId)
      .then((w) => mine === seq.current && setData(w))
      .catch((err: Error) => mine === seq.current && setError(err.message));
  }, [agentId, merchantId, version]);

  const open = <Link to={evaluationPath('', agentId)}>Open in Scenario Intelligence →</Link>;
  const muted = isDark ? colors.slate[400] : colors.slate[500];

  if (error)
    return (
      <SectionCard title="Latest evaluation">
        <Text type="secondary" style={{ fontSize: 13 }}>{`The latest evaluation could not be read: ${error}`}</Text>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => setVersion((v) => v + 1)}>
            Retry
          </Button>
          {open}
        </div>
      </SectionCard>
    );
  if (!data)
    return (
      <SectionCard title="Latest evaluation">
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      </SectionCard>
    );

  const c = data.counts;
  const active = c.library + data.exploratory.drafts;
  const edited = data.notAssessed.find((g) => g.kind === 'EditedSinceRun')?.count ?? 0;
  const uncovered = data.coverage.uncovered;
  const facts: string[] = [];
  if (uncovered > 0) facts.push(`${uncovered} scenario categor${uncovered === 1 ? 'y has' : 'ies have'} no scenario.`);
  if (edited > 0) facts.push(`${edited} scenario${edited === 1 ? ' was' : 's were'} edited since ${edited === 1 ? 'its' : 'their'} last run.`);
  if (data.running) facts.push('A run is in progress.');

  return (
    <SectionCard
      title={
        <span data-testid="latest-evaluation">
          Latest evaluation{' '}
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {data.latestRunId != null
              ? `run #${data.latestRunId}${data.latestRunUtc ? ` · ${dayjs(data.latestRunUtc).format('D MMM HH:mm')}` : ''} · ${c.library} library scenario${c.library === 1 ? '' : 's'}`
              : `no run in this practice yet · ${c.library} library scenario${c.library === 1 ? '' : 's'}`}
          </Text>
        </span>
      }
    >
      {active === 0 ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          This agent has no scenario yet. Evaluate this agent to generate some for review.
        </Text>
      ) : (
        <div className="si-eval-sum" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
          {STATE_ORDER.map((state) => (
            <div key={state} data-testid={`latest-${state}`}>
              <div style={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: countOf(c, state) === 0 || state === 'NotAssessed' ? undefined : STATE_ACCENT[state] }}>
                {countOf(c, state)}
              </div>
              <div style={{ fontSize: 12, color: muted }}>{RESULT_STATE_LABEL[state]}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`, margin: '12px 0 10px' }} />
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: 12.5, flex: '1 1 240px' }}>
          {facts.length ? facts.join(' ') : 'Counted once each over the library’s current versions, in this practice.'}
          {data.exploratory.drafts > 0 ? ` ${data.exploratory.drafts} draft${data.exploratory.drafts === 1 ? ' is' : 's are'} apart, as Exploratory.` : ''}
        </Text>
        <span style={{ fontSize: 12.5 }}>{open}</span>
      </div>
    </SectionCard>
  );
});
